import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, PowerOff, Trash2 } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Shared bulk-action logic for list pages (enable / disable / delete).
// For advertisers/products, delete may surface a 409 (active offers) -> confirm dialog.
export function useBulkActions(resource, { noun = "item" } = {}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(null); // { ids, activeOffers }
  const [busy, setBusy] = useState(false);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [resource] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["options"] });
  };

  const call = (action, ids, mode) => http.post(`/${resource}/bulk`, { action, ids, mode });

  const handleBulk = async (action, ids) => {
    if (!ids?.length) return;
    try {
      if (action === "delete") {
        try {
          const { data } = await call("delete", ids);
          invalidate();
          toast.success(`Deleted ${data.affected} ${noun}${data.affected === 1 ? "" : "s"}`);
        } catch (e) {
          if (e.response?.status === 409) {
            setPending({ ids, activeOffers: e.response.data?.detail?.active_offers || 0 });
            return;
          }
          throw e;
        }
      } else {
        const { data } = await call(action, ids);
        invalidate();
        toast.success(`${action === "enable" ? "Enabled" : "Disabled"} ${data.affected} ${noun}${data.affected === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmDisableDelete = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { data } = await call("delete", pending.ids, "disable_offers");
      invalidate();
      toast.success(`Deleted ${data.affected} ${noun}${data.affected === 1 ? "" : "s"}`);
      setPending(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return { handleBulk, pending, setPending, confirmDisableDelete, busy };
}

export function BulkDeleteOffersDialog({ pending, onCancel, onConfirm, busy, noun = "item" }) {
  const open = !!pending;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <AlertTriangle className="h-4 w-4 text-[#F59E0B]" /> Delete {pending?.ids?.length} {noun}{pending?.ids?.length === 1 ? "" : "s"}?
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            The selected {noun}{pending?.ids?.length === 1 ? " is" : "s are"} linked to <span className="font-semibold text-[#F59E0B]">{pending?.activeOffers}</span> active
            offer{pending?.activeOffers === 1 ? "" : "s"}. To continue, the related offers must be disabled first.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3 text-sm text-muted-foreground">
          <PowerOff className="mt-0.5 h-4 w-4 shrink-0" />
          All related offers will be set to <span className="font-medium text-foreground">disabled</span>, then the selected {noun}{pending?.ids?.length === 1 ? "" : "s"} will be deleted.
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid="bulk-delete-cancel" className="text-muted-foreground">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} data-testid="bulk-delete-confirm"
            className="gap-1.5 bg-[#EF4444] text-white hover:bg-[#dc2626]">
            <Trash2 className="h-4 w-4" /> {busy ? "Deleting..." : "Disable offers & delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
